/**
 * The Stockfish worker's ONE mount point (fix D / R4).
 *
 * `rail/Rail/View.tsx` renders it only while `analysisStore.on`, so the engine's
 * whole lifecycle is this component's: mounting it spawns the worker and runs the
 * uci/isready handshake, unmounting it calls `destroy()`. That is why the
 * fair-play rule can be asserted structurally — with a live game on the board
 * there is no `StockfishEngine` in the tree, not merely a paused one.
 *
 * It renders nothing (`StockfishEngine` returns null).
 *
 * F6 (fix F-B, 2026-10-06): `onStatus` mirrors the worker's lifecycle onto
 * `engineStatus.ts` so a failure (today: the `stockfish` package missing from
 * this install, ARCHITECTURE §13) is never silent — `AnalysisHeader` reads it.
 * The callback is a STABLE module-level function (never a new identity per
 * render) so it cannot itself be a reason for `StockfishEngine`'s own effects
 * to re-run; the store resets to 'off' on unmount, the same moment the worker
 * this status describes stops existing.
 */
import React, {useEffect} from 'react';

import StockfishEngine from '@/Components/StockfishEngine';
import type {EngineStatus} from '@/Components/StockfishEngine';

import {useEngineStatus} from './engineStatus';
import {useEngine} from './useEngine';

const onStatus = (status: EngineStatus, detail?: string): void => {
  useEngineStatus.getState().setStatus(status, detail);
};

export default React.memo(function EngineHost() {
  const {
    fen,
    depth,
    variations,
    movetime,
    searchEnabled,
    onResult,
    onBestMove,
  } = useEngine();

  useEffect(() => {
    return () => {
      useEngineStatus.getState().reset();
    };
  }, []);

  return (
    <StockfishEngine
      engineOn
      fen={fen}
      depth={depth}
      variations={variations}
      movetime={movetime}
      searchEnabled={searchEnabled}
      onResult={onResult}
      onBestMove={onBestMove}
      onStatus={onStatus}
    />
  );
});
